import { useState } from "react";
import { ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BookingProvider,
  BookingProviderType,
  bookingProviders,
} from "@/types/bookingProviders";
import { cn } from "@/lib/utils";
import { toTitleCase } from "@/lib/general";
import { TripTransport } from "@/types/trip";
import { TripLodging } from "@/types/accommodation";

// Import all logos
import waytoraLogo from "@/assets/logos/internal/sidebar.svg";
import kiwiLogo from "@/assets/logos/external/kiwi-logo.svg";
import viatorLogo from "@/assets/logos/external/viator-logo.svg";
import agodaLogo from "@/assets/logos/external/agoda-logo.svg";
import bookingLogo from "@/assets/logos/external/booking-com-logo.svg";
import plumGuideLogo from "@/assets/logos/external/plum-guide-logo.svg";

const logoMap: Record<string, string> = {
  waytora: waytoraLogo,
  booking: bookingLogo,
  agoda: agodaLogo,
  viator: viatorLogo,
  kiwi: kiwiLogo,
  plumguide: plumGuideLogo,
};

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: BookingProviderType;
  tripId?: string;
  transportData?: TripTransport;
  activityData?: {
    id: string;
    name: string;
    customType?: string;
  };
  lodgingData?: {
    id: string;
    name: string;
    checkIn: string;
    checkOut: string;
    type: TripLodging["type"];
  };
  onBooked?: () => void;
}

export function BookingModal({
  isOpen,
  onClose,
  type,
  tripId,
  transportData,
  activityData,
  lodgingData,
  onBooked,
}: BookingModalProps) {
  const [selectedProvider, setSelectedProvider] =
    useState<BookingProvider | null>(null);

  const availableProviders = bookingProviders.filter((provider) => {
    // First check if provider supports accommodation type
    if (!provider.type.includes(type)) return false;

    // For lodging, filter based on lodging type
    if (type === "accommodation" && lodgingData) {
      if (lodgingData.type === "home") {
        // Only show Plum Guide for homes
        return provider.id === "plumguide";
      } else {
        // For hotels and custom, show all except Plum Guide
        return provider.id !== "plumguide";
      }
    }

    return true;
  });

  const handleProviderClick = (provider: BookingProvider) => {
    if (provider.isComingSoon) return;
    setSelectedProvider(provider);

    if (provider.id === "kiwi" && transportData) {
      // Construct Kiwi-specific URL with flight details
      const params = new URLSearchParams({
        name: `Flight to ${
          transportData.legs[transportData.legs.length - 1]?.arrival.location
            .name || ""
        }`,
        fromLocation: transportData.legs[0]?.departure.location.name || "",
        toLocation:
          transportData.legs[transportData.legs.length - 1]?.arrival.location
            .name || "",
        airlines: transportData.legs.map((leg) => leg.provider.code).join(","),
      });

      const url = `/booking/${tripId}/transport/${
        transportData?.id
      }?${params.toString()}`;

      window.open(url, "_blank");
    } else if (provider.id === "viator" && activityData) {
      // Construct Viator-specific URL with activity details
      const params = new URLSearchParams({
        name: activityData.name,
      });

      const url = `/booking/${tripId}/activity/${
        activityData.id
      }?${params.toString()}`;

      window.open(url, "_blank");
    } else if (
      (provider.id === "booking" ||
        provider.id === "agoda" ||
        provider.id === "plumguide") &&
      lodgingData
    ) {
      const params = new URLSearchParams({
        name: lodgingData.name,
        checkIn: lodgingData.checkIn,
        checkOut: lodgingData.checkOut,
        type: lodgingData.type,
      });

      const url = `/booking/${tripId}/accommodation/${
        lodgingData.id
      }?${params.toString()}`;

      window.open(url, "_blank");
    }

    if (onBooked) {
      onBooked();
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader className="mb-2">
          <DialogTitle className="text-xl font-semibold">
            Book Your {toTitleCase(activityData?.customType || type)}
          </DialogTitle>
          <DialogDescription className="text-neutral-600 text-sm font-light mt-2">
            Select your preferred booking platform
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {availableProviders.map((provider) => (
            <button
              key={provider.id}
              onClick={() => handleProviderClick(provider)}
              className={cn(
                "w-full relative flex items-center gap-4 px-4 py-3 rounded-full ring-1 ring-neutral-200 hover:ring-neutral-400 transition-all duration-200",
                "group",
                provider.isComingSoon &&
                  "cursor-default ring-neutral-200 hover:ring-neutral-200"
              )}
            >
              <div
                className={cn(
                  "relative w-10 h-10 flex-shrink-0",
                  provider.isComingSoon && "opacity-50"
                )}
              >
                <img
                  src={logoMap[provider.id]}
                  alt={`${provider.name} logo`}
                  className={cn(
                    "w-full h-full object-contain",
                    provider.id === "waytora" &&
                      "border-2 rounded-full border-blue-400 p-1.5",
                    provider.id === "viator" && "rounded-full",
                    provider.id === "booking" && "rounded-full",
                    provider.id === "kiwi" && "scale-110",
                    provider.id === "agoda" && "scale-90",
                    provider.id === "plumguide" && "bg-[#FFBB30] rounded-full"
                  )}
                />
              </div>

              <span
                className={cn(
                  "text-sm font-normal text-neutral-700 flex-1 text-left",
                  provider.isComingSoon && "opacity-50"
                )}
              >
                {provider.name}
              </span>

              {provider.isComingSoon && (
                <span className="text-xs font-medium text-blue-600 bg-blue-50 px-3 py-2 rounded-full">
                  Coming Soon
                </span>
              )}

              {!provider.isComingSoon && (
                <ExternalLink className="w-4 h-4 text-neutral-400 group-hover:text-neutral-600 transition-all duration-200" />
              )}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
